import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api from "../services/api";
import { useHotel } from "../contexts/HotelContext";
import { detectBrandByDomain } from "../services/brandApi";

const PasswordReset: React.FC = () => {
  const { currentHost } = useHotel();
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [brandName, setBrandName] = useState("");

  useEffect(() => {
    const fetchBrandName = async () => {
      try {
        const brand = await detectBrandByDomain(currentHost);
        setBrandName(brand.displayName || "Hotel B2B");
      } catch (error) {
        console.error("Failed to fetch brand name:", error);
      }
    };
    fetchBrandName();
  }, [currentHost]);

  useEffect(() => {
    // Extract token from URL
    const params = new URLSearchParams(location.search);
    const urlToken = params.get("token");

    if (!urlToken) {
      setMessage("Brak tokena resetującego. Link jest nieprawidłowy.");
      setIsVerifying(false);
      return;
    }

    setToken(urlToken);

    // Verify token on mount
    const verifyToken = async () => {
      try {
        const { data } = await api.get("/auth/verify-reset-token", {
          params: { token: urlToken },
        });

        if (data.success) {
          setTokenValid(true);
          setUserEmail(data.email);
        } else {
          setMessage(data.error || "Token jest nieprawidłowy lub wygasł");
        }
      } catch (error) {
        setMessage("Token jest nieprawidłowy lub wygasł");
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");

    // Validation
    if (newPassword !== confirmPassword) {
      setMessage("Hasła nie są identyczne");
      return;
    }

    if (newPassword.length < 8) {
      setMessage("Hasło musi mieć minimum 8 znaków");
      return;
    }

    setIsLoading(true);

    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        newPassword,
      });

      if (data.success) {
        setIsSuccess(true);
        setMessage("Hasło zostało pomyślnie zresetowane!");

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message;
      setMessage(
        typeof errorMessage === "string"
          ? errorMessage
          : "Wystąpił błąd podczas resetowania hasła"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isVerifying) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-neutral-900">
            Weryfikacja linku...
          </h2>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 bg-error-600 rounded-full flex items-center justify-center mb-6">
              <svg
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Link nieprawidłowy
            </h2>
            <p className="text-neutral-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Link
                to="/auth/forgot-password"
                className="block w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
              >
                Wyślij nowy link resetujący
              </Link>
              <Link
                to="/login"
                className="block w-full text-primary-600 hover:text-primary-700 font-medium"
              >
                Powrót do logowania
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 bg-success-600 rounded-full flex items-center justify-center mb-6">
              <svg
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Hasło zresetowane!
            </h2>
            <p className="text-neutral-600 mb-6">
              Twoje hasło zostało pomyślnie zmienione. Za chwilę zostaniesz
              przekierowany do strony logowania.
            </p>
            <Link
              to="/login"
              className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
            >
              Przejdź do logowania
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center mb-6">
            <svg
              className="h-8 w-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
              />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-neutral-900">
            Ustaw nowe hasło
          </h2>
          {brandName && (
            <p className="mt-2 text-sm text-neutral-600">{brandName}</p>
          )}
          <p className="mt-2 text-sm text-neutral-600">
            Dla konta: <strong>{userEmail}</strong>
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                Nowe hasło
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Minimum 8 znaków"
              />
              <p className="mt-1 text-xs text-neutral-500">
                Hasło musi mieć minimum 8 znaków
              </p>
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                Potwierdź hasło
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Powtórz nowe hasło"
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Resetowanie...
                  </div>
                ) : (
                  "Zresetuj hasło"
                )}
              </button>
            </div>

            {message && !isSuccess && (
              <div className="text-center text-sm font-medium text-error-600">
                {message}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
